import { useQuery } from "@apollo/client";
import { useAtom } from "jotai";
import router from "next/router";
import { createContext, ReactNode, useEffect, useMemo } from "react";

import { initializeApollo } from "@/apollo-client";
import ME from "@/graphql/queries/me";
import { preferenceOriginAtom } from "@/hooks/useTracker";
import { ACCESS_TOKEN_KEY } from "@/utils/const";
import { encodeURL } from "@/utils/index";
export const SEARCH_COUNT_KEY = "plush-search-count";

const MAX_SEARCH_COUNT = 2;

const apolloClient = initializeApollo();

export interface MeContextValue {
  me: any;
  featureFlags: Record<string, boolean>;
  isPreferenceSet: boolean;
  fetchMe: () => void;
  logout: () => void;
  setAccessToken: (value: string) => void;
  goLogin: () => void;
  checkSearchCount: (redirectURL?: string) => boolean;
}

interface MeProviderProps {
  children?: ReactNode;
}

export const MeContext = createContext<MeContextValue>({
  me: null,
  isPreferenceSet: false,
  featureFlags: {},
  fetchMe: () => {},
  logout: () => {},
  setAccessToken: () => {},
  goLogin: () => {},
  checkSearchCount: () => false,
});

const MeProvider = ({ children }: MeProviderProps) => {
  //   const [me, setMe] = useState(null);
  const [, setPreferenceOrigin] = useAtom(preferenceOriginAtom);
  const { data, refetch } = useQuery(ME, {
    errorPolicy: "ignore",
  });

  // Migration: Move access token from localStorage to cookies
  useEffect(() => {
    if (typeof window !== "undefined" && localStorage) {
      const oldToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (oldToken) {
        // Set the token in cookies
        setAccessToken(oldToken);
        // Remove the old token from localStorage
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        console.log("Migrated access token from localStorage to cookies");
      }
    }
  }, []); // Run once on mount

  const me = useMemo(() => {
    if (data) {
      return data.me;
    }
    return null;
  }, [data]);

  const featureFlags = useMemo(() => {
    if (data) {
      return data.featureFlags;
    }
    return null;
  }, [data]);

  const showPreferencePopup = useMemo(() => {
    if (data) {
      return data?.featureFlags?.showPreferencePopup;
    }
    return false;
  }, [data]);

  useEffect(() => {
    if (data?.me?.id !== undefined) {
      // Set Amplitude user ID
      import("@amplitude/analytics-browser").then(({ setUserId }) => {
        setUserId(data?.me?.id);
      });

      // Set Customer.io user ID
      import("@customerio/cdp-analytics-browser").then(
        ({ AnalyticsBrowser }) => {
          const analytics = AnalyticsBrowser.load({
            writeKey: "61a29fca6134b05a8d3a",
          });
          analytics.identify(data?.me?.id, {
            email: data?.me?.privateInfo?.email,
            name: data?.me?.name,
            created_at: data?.me?.privateInfo?.createdAt,
          });
        }
      );
    }
  }, [data?.me?.id]);

  useEffect(() => {
    if (!!me && localStorage) {
      localStorage.setItem(SEARCH_COUNT_KEY, "0");
    }
  }, [me]);

  useEffect(() => {
    if (showPreferencePopup) {
      setPreferenceOrigin("site-open");
      router.push("/preference");
    }
  }, [showPreferencePopup]);

  const setAccessToken = (value: string) => {
    if (typeof document !== "undefined") {
      // Set cookie to expire in 30 days
      const maxAge = 30 * 24 * 60 * 60; // 30 days in seconds
      document.cookie = `${ACCESS_TOKEN_KEY}=${value}; path=/; secure; samesite=None; max-age=${maxAge}`;
    }
    return;
  };

  const goLogin = (redirectURL?: string) => {
    if (router) {
      router.push(
        `/login?redirectURL=${encodeURL(redirectURL || router.asPath)}`
      );
    }
  };

  const fetchMe = async () => {
    return await refetch();
  };

  const logout = async () => {
    document.cookie = `${ACCESS_TOKEN_KEY}=; path=/; max-age=0`;
    window.sessionStorage.clear();
    apolloClient.resetStore();
  };

  const checkSearchCount = (redirectURL?: string) => {
    if (localStorage && !me) {
      const searchCount = localStorage.getItem(SEARCH_COUNT_KEY);
      if (searchCount) {
        const count = Number(searchCount);
        if (count >= MAX_SEARCH_COUNT) {
          if (router) {
            router.push(
              `/login?searchCount=over&redirectURL=${
                redirectURL || router.asPath
              }`
            );
            return true;
          }
        } else {
          localStorage.setItem(SEARCH_COUNT_KEY, (count + 1).toString());
        }
      } else {
        localStorage.setItem(SEARCH_COUNT_KEY, "1");
      }
    }
    return false;
  };

  const value = {
    fetchMe,
    me,
    featureFlags,
    setAccessToken,
    logout,
    goLogin,
    checkSearchCount,
    isPreferenceSet: !!data?.isPreferenceSet,
  };

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
};

export default MeProvider;
